import { Stack, Tooltip } from '@mui/material'
import Chip from '@mui/material/Chip'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

/**
 * Role-source + screener-profile badges for the "All Screeners (by role)" tab. A screener is
 * a screener via either of two sources — ROLE_SCREENER in User.authorities (method 1) and/or a
 * teacherUnEditableFields.servicesApproved.ROLE_SCREENER block on the practitioner (method 2) —
 * and the tab exists to make the gaps obvious. Each chip reads off the flags the
 * /screeners/role-holders/list endpoint attaches to the row.
 *
 *  - green  → source present / profile ready
 *  - amber  → present but disabled (service block off, or profile paused)
 *  - red    → missing (the gap this tab is meant to surface)
 */
export default function ScreenerRoleSourceBadges({ practitioner }) {
  if (!practitioner) return null

  const {
    hasUserScreenerRole,
    hasServiceApprovedScreenerRole,
    serviceApprovedScreenerEnabled,
    hasScreenerProfile,
    screenerProfileEnabled,
    hasPractitionerRecord,
  } = practitioner

  const badges = []

  // Method 1 — User.authorities ROLE_SCREENER
  badges.push(
    hasUserScreenerRole
      ? {
          key: 'user-role',
          color: 'success',
          icon: <CheckCircleIcon sx={{ fontSize: '14px' }} />,
          label: 'User ROLE_SCREENER',
          tip: 'ROLE_SCREENER is present in this user’s User.java authorities (method 1).',
        }
      : {
          key: 'user-role',
          color: 'error',
          icon: <ErrorOutlineIcon sx={{ fontSize: '14px' }} />,
          label: 'Missing User ROLE_SCREENER',
          tip: 'ROLE_SCREENER is NOT in this user’s User.java authorities (method 1). Grant it so screening features that gate on the user role work.',
        },
  )

  // Method 2 — servicesApproved.ROLE_SCREENER
  if (!hasServiceApprovedScreenerRole) {
    badges.push({
      key: 'service-role',
      color: 'error',
      icon: <ErrorOutlineIcon sx={{ fontSize: '14px' }} />,
      label: 'Missing service ROLE_SCREENER',
      tip: 'Practitioner.teacherUnEditableFields.servicesApproved has no ROLE_SCREENER block (method 2). A Screener Profile cannot be created until this is enabled.',
    })
  } else if (serviceApprovedScreenerEnabled) {
    badges.push({
      key: 'service-role',
      color: 'success',
      icon: <CheckCircleIcon sx={{ fontSize: '14px' }} />,
      label: 'Service ROLE_SCREENER',
      tip: 'servicesApproved.ROLE_SCREENER is present and enabled (method 2).',
    })
  } else {
    badges.push({
      key: 'service-role',
      color: 'warning',
      icon: <WarningAmberIcon sx={{ fontSize: '14px' }} />,
      label: 'Service ROLE_SCREENER disabled',
      tip: 'servicesApproved.ROLE_SCREENER exists but enabled=false (method 2). Enable it before creating a Screener Profile.',
    })
  }

  // Screener Profile presence
  if (!hasScreenerProfile) {
    badges.push({
      key: 'profile',
      color: 'error',
      icon: <ErrorOutlineIcon sx={{ fontSize: '14px' }} />,
      label: 'No Screener Profile',
      tip: 'No Screener Profile exists for this screener yet.',
    })
  } else if (screenerProfileEnabled) {
    badges.push({
      key: 'profile',
      color: 'success',
      icon: <CheckCircleIcon sx={{ fontSize: '14px' }} />,
      label: 'Screener Profile',
      tip: 'A Screener Profile exists and is enabled.',
    })
  } else {
    badges.push({
      key: 'profile',
      color: 'warning',
      icon: <WarningAmberIcon sx={{ fontSize: '14px' }} />,
      label: 'Screener Profile paused',
      tip: 'A Screener Profile exists but is disabled.',
    })
  }

  if (hasPractitionerRecord === false) {
    badges.push({
      key: 'no-practitioner',
      color: 'error',
      icon: <ErrorOutlineIcon sx={{ fontSize: '14px' }} />,
      label: 'No practitioner record',
      tip: 'This ROLE_SCREENER user has no practitioner (teacher) record, so most actions and Screener Profile creation are unavailable until one is onboarded.',
    })
  }

  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
      {badges.map((badge) => (
        <Tooltip key={badge.key} title={badge.tip} arrow>
          <Chip
            size="small"
            color={badge.color}
            variant={badge.color === 'success' ? 'outlined' : 'filled'}
            icon={badge.icon}
            label={badge.label}
            sx={{
              height: '22px',
              fontSize: '0.6875rem',
              fontWeight: 600,
              '& .MuiChip-icon': { ml: '4px' },
            }}
          />
        </Tooltip>
      ))}
    </Stack>
  )
}
